import React from 'react';
import { getPmsToolbarIcon } from '../shared/pmsToolbarIcon';
import { createPmsGetTableActions } from '../shared/pmsTableActions';
import { Column } from '../../../components/DataTable/DataTable';
import Badge from '../../../components/ui/Badge';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { PmsDepartmentType } from './useData';

export const moduleConfig = {
  module: {
    title: "Department types",
    permissionModuleKey: 'pms_department_types',
    route: {
      list: '/pms/pms-department-types',
      new: '/pms/pms-department-types/new',
      detail: (id: number) => `/pms/pms-department-types/${id}`,
      edit: (id: number) => `/pms/pms-department-types/${id}/edit`},
    api: {
      list: '/pms-department-types',
      detail: (id: number) => `/pms-department-types/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by code or name…' },
  filterGroups: [{ name: 'basic', label: 'Basic', icon: '', fields: ['name', 'isActive'] }],
  filters: {
    fields: [
      { key: 'name', label: 'Name', placeholder: 'All names', getValue: (item: PmsDepartmentType) => item.name, options: [] },
      {
        key: 'isActive',
        label: 'Active',
        type: 'boolean' as const,
        getValue: (item: PmsDepartmentType) => (item.isActive ? 'Yes' : 'No'),
        options: [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]},
    ],
    applyFilter: (items: PmsDepartmentType[], filters: Record<string, string[]>) =>
      items.filter((item) => {
        if (filters.name?.length) {
          const v = item.name || '';
          if (!filters.name.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }
        if (filters.isActive?.length) {
          const s = item.isActive ? 'Yes' : 'No';
          if (!filters.isActive.includes(s)) return false;
        }
        return true;
      })},
  toolbar: {
    actions: [
      { key: 'add', icon: 'add', title: 'New', showTitle: false, variant: 'primary' as const, href: '/pms/pms-department-types/new' },
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: PmsDepartmentType) => item.id,
  },
  emptyStates: {
    noData: 'No department types yet.',
    noSearchResults: 'No matches.',
    noFilterResults: 'No matches for filters.',
  },
  delete: {
    title: 'Delete',
    message: 'Delete this item?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deletePmsDepartmentType } = await import('../../../services/mutations');
      await deletePmsDepartmentType(id);
    }},
  utils: { formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '') },
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This item does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: "Department types",
    getTitle: (item: PmsDepartmentType) =>
      item.name ? item.name : `#${item.id}`},
  form: {
    new: { title: 'New', subtitle: 'Code and display name', submitText: 'Create' },
    edit: { title: 'Edit', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    create: async (data: Partial<PmsDepartmentType>) => {
      const { createPmsDepartmentType } = await import('../../../services/mutations');
      return createPmsDepartmentType(data as Record<string, unknown>);
    },
    update: async (id: number, data: Partial<PmsDepartmentType>) => {
      const { updatePmsDepartmentType } = await import('../../../services/mutations');
      return updatePmsDepartmentType(id, data as Record<string, unknown>);
    }},
  fields: [
    { name: 'code', label: 'Code', type: 'text', required: true, validation: { required: true, minLength: 1, maxLength: 40 } },
    { name: 'name', label: 'Name', type: 'text', required: true, validation: { required: true, minLength: 1, maxLength: 100 } },
    { name: 'sortOrder', label: 'Sort order', type: 'number', defaultValue: 0 },
    { name: 'isActive', label: 'Active', type: 'switch', switchLabel: 'Active', defaultValue: true },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'main',
        title: "Department types",
        icon: '',
        gridCols: 'md:grid-cols-12',
        fields: ['code', 'name', 'sortOrder', 'isActive'],
        fieldSpans: { code: 'md:col-span-4', name: 'md:col-span-8', sortOrder: 'md:col-span-3', isActive: 'md:col-span-6' },
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<PmsDepartmentType>[] => [
  { key: 'code', header: 'Code', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v} /> },
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v, row) => (
      <Badge variant={v ?? row?.isActive ? 'success' : 'danger'} className="shrink-0 border rounded-md px-2 py-0.5">
        {v ?? row?.isActive ? 'Yes' : 'No'}
      </Badge>
    )},
  { key: 'sortOrder', header: 'Order', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

export const getDetailSections = (item: PmsDepartmentType, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Code', value: item.code },
      { label: 'Name', value: item.name },
      { label: 'Sort order', value: String(item.sortOrder) },
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt) },
    ]},
];

export const getStatusBadges = () => null;
export const getInitialFormData = (): Partial<PmsDepartmentType> => ({ code: '', name: '', sortOrder: 0, isActive: true });
export const mapItemToFormData = (item: PmsDepartmentType): Partial<PmsDepartmentType> => ({
  code: item.code ?? '',
  name: item.name ?? '',
  sortOrder: item.sortOrder ?? 0,
  isActive: item.isActive ?? true});
export const getToolbarIcon = getPmsToolbarIcon;
export const getTableActions = createPmsGetTableActions(moduleConfig.module.route);
